import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@telegram-apps/telegram-ui";
import Phaser from "phaser";
import UIPlugin from "phaser3-rex-plugins/templates/ui/ui-plugin.js";
import Game from "../../scripts/game/Game";
import { useGameStore, selectIsInitialized, selectIsLoading, selectError } from "../../store/gameStore";
import LevelUpScene from "../../scripts/scenes/LevelUpScene";
import GameOverScene from "../../scripts/scenes/GameOverScene";
import TreasureScene from "../../scripts/scenes/TreasureScene";
import MainScene from "../../scripts/scenes/MainScene";
import PreloadScene from "../../scripts/scenes/PreloadScene";
import PauseScene from "../../scripts/scenes/PauseScene";
import { DEFAULT_SCALE } from "../../scripts/consts/UI";
import "./Game.css";

// Declare global property for stage selection
declare global {
  interface Window {
    selectedStageFromURL?: string;
    hideLoadingScreen?: () => void;
    showLoadingScreen?: () => void;
  }
}

// Function to calculate app dimensions
function calculateAppDimensions() {
  const DEFAULT_WIDTH = window.innerWidth * DEFAULT_SCALE;
  const DEFAULT_HEIGHT = window.innerHeight * DEFAULT_SCALE;
  const IS_VERTICAL_SCREEN = window.innerWidth <= window.innerHeight;
  const scaleRatio = DEFAULT_SCALE;

  let APP_WIDTH = Math.floor(window.innerWidth * scaleRatio);
  let APP_HEIGHT = Math.floor(window.innerHeight * scaleRatio);

  if (!IS_VERTICAL_SCREEN) {
    APP_HEIGHT = DEFAULT_HEIGHT;
    APP_WIDTH = Math.min(3 * DEFAULT_WIDTH, window.innerWidth);

    if (APP_WIDTH > 1.6 * APP_HEIGHT) {
      APP_WIDTH = 1.6 * APP_HEIGHT;
    }
  }

  return { APP_WIDTH, APP_HEIGHT };
}

// Global game instance
let gameInstance: Game | null = null;

export function setGameInstance(instance: Game | null) {
  gameInstance = instance;
}

export function getGameInstance() {
  return gameInstance;
}

// Canvas interactivity functions
export function disableCanvasInteractivity() {
  if (!gameInstance) return;

  Object.values(gameInstance.scene.scenes).forEach((scene) => {
    if (scene.input?.enabled) {
      scene.input.enabled = false;
    }
  });
}

export function enableCanvasInteractivity() {
  if (!gameInstance) return;

  Object.values(gameInstance.scene.scenes).forEach((scene) => {
    if (!scene.input?.enabled) {
      scene.input.enabled = true;
    }
  });
}

export function GameComponent() {
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isInitialized = useGameStore(selectIsInitialized);
  const isLoading = useGameStore(selectIsLoading);
  const error = useGameStore(selectError);
  const [dimensions, setDimensions] = useState<{
    APP_WIDTH: number;
    APP_HEIGHT: number;
  } | null>(null);

  // Calculate dimensions when component mounts and handle resize
  useEffect(() => {
    const updateDimensions = () => {
      const newDimensions = calculateAppDimensions();
      setDimensions(newDimensions);
    };

    // Calculate initial dimensions
    updateDimensions();

    // Add resize listener
    const handleResize = () => {
      updateDimensions();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // Wait for services to be initialized and dimensions to be calculated
    if (!isInitialized || isLoading || error || !dimensions) {
      return;
    }

    if (!gameContainerRef.current) return;

    const { APP_WIDTH, APP_HEIGHT } = dimensions;

    // Check if a stage was specified in URL parameters
    const stageParam = searchParams.get("stage");
    if (stageParam) {
      // Store the stage selection globally so PreloadScene can access it
      window.selectedStageFromURL = stageParam;
    }

    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      backgroundColor: "#222c3a",
      scale: {
        parent: gameContainerRef.current,
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: APP_WIDTH,
        height: APP_HEIGHT,
      },
      render: {
        pixelArt: true,
        antialias: false,
        roundPixels: true,
        transparent: false,
        clearBeforeRender: true,
        preserveDrawingBuffer: false,
        failIfMajorPerformanceCaveat: false,
        powerPreference: "high-performance",
        batchSize: 4096,
      },
      fps: {
        target: 30,
        limit: 40,
        deltaHistory: 10,
        panicMax: 120,
        smoothStep: true,
      },
      scene: [
        PreloadScene,
        MainScene,
        LevelUpScene,
        GameOverScene,
        TreasureScene,
        PauseScene,
      ],
      physics: {
        default: "arcade",
        arcade: {
          debug: false,
          gravity: { x: 0, y: 0 },
          fps: 60,
          timeScale: 1,
        },
      },
      plugins: {
        scene: [
          {
            key: "rexUI",
            plugin: UIPlugin,
            mapping: "rexUI",
          },
        ],
      },
      audio: {
        noAudio: false,
        disableWebAudio: false,
      },
      dom: {
        createContainer: true,
        pointerEvents: "all",
      },
    };

    try {
      const game = new Game(config);
      setGameInstance(game);

      // Listen for game events that should navigate back to menu
      const handleGameEnd = () => {
        navigate("/menu");
      };

      // You can add event listeners here for game end events
      // game.events.on('gameEnd', handleGameEnd);

      return () => {
        if (game) {
          game.destroy(true);
          setGameInstance(null);
        }
      };
    } catch (error) {
      console.error("Failed to initialize game:", error);
      navigate("/menu");
    }
  }, [navigate, searchParams, isInitialized, isLoading, error, dimensions]);

  // Show error state if services failed to initialize
  if (error) {
    return (
      <div className="game-page">
        <div className="game-error">
          <h2>Failed to Load Game</h2>
          <p>Error: {error}</p>
          <Button
            className="game-error-button"
            size="s"
            mode="filled"
            onClick={() => navigate("/menu")}
          >
            Return to Menu
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="game-page">
      <div ref={gameContainerRef} className="game-container" />
    </div>
  );
}
